import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { Subscription } from 'rxjs';
import { CustomerAccountType } from 'src/app/_enums/customer-account-type.enum';
import { EntityType } from 'src/app/_enums/entity-type.enum';
import { FamilyMemberType } from 'src/app/_enums/family-member-type.enum';
import {
  BUSINESS_INSURANCES,
  getInsuranceTypeLabel,
  InsuranceType,
  PERSONAL_INSURANCES,
  sortInsuranceTypesByLabel,
} from 'src/app/_enums/insurance-type.enum';
import {
  EXCLUSIVE_CHANGE_REASON_GROUPS,
  getLeadChangeReasonLabel,
  isKnownLeadChangeReason,
  LeadChangeReason,
  normalizeChangeReason,
} from 'src/app/_enums/lead-change-reason.enum';
import {
  getGlobalLeadStatus,
  getLeadStatusValues,
  LeadStatus,
} from 'src/app/_enums/lead-status.enum';
import { PolicyStatus, ProposalState } from 'src/app/_enums/proposal.enums';
import { UserAuthService } from 'src/app/_services/user-auth.service';
import { FamilyMember, User } from 'src/app/_types/auth.types';
import { Lead } from 'src/app/_types/lead.types';
import {
  ProposalDetails,
  UpsertLeadRequest,
} from 'src/app/_types/proposals.type';

import { AnimalFormComponent } from './animal-form/animal-form.component';
import { BenefitsAccountFormComponent } from './benefits-account-form/benefits-account-form.component';
import { BuildingFormComponent } from './building-form/building-form.component';
import { BusinessAccidentFormComponent } from './business-forms/business-accident-form/business-accident-form.component';
import { BusinessConstructionFormComponent } from './business-forms/business-construction-form/business-construction-form.component';
import { BusinessConstructionWarrantyFormComponent } from './business-forms/business-construction-warranty-form/business-construction-warranty-form.component';
import { BusinessCyberFormComponent } from './business-forms/business-cyber-form/business-cyber-form.component';
import { BusinessFleetFormComponent } from './business-forms/business-fleet-form/business-fleet-form.component';
import { BusinessHealthFormComponent } from './business-forms/business-health-form/business-health-form.component';
import { BusinessLegalProtectionFormComponent } from './business-forms/business-legal-protection-form/business-legal-protection-form.component';
import { BusinessRentalFormComponent } from './business-forms/business-rental-form/business-rental-form.component';
import { BusinessRetirementFormComponent } from './business-forms/business-retirement-form/business-retirement-form.component';
import { BusinessTechnicalInstallationFormComponent } from './business-forms/business-technical-installation-form/business-technical-installation-form.component';
import { BusinessThingsFormComponent } from './business-forms/business-things-form/business-things-form.component';
import { BusinessTransportFormComponent } from './business-forms/business-transport-form/business-transport-form.component';
import { BusinessVehicleFormComponent } from './business-forms/business-vehicle-form/business-vehicle-form.component';
import { RcCompanyFormComponent } from './business-forms/rc-company-form/rc-company-form.component';
import { SharedBusinessFormComponent } from './business-forms/shared-business-form/shared-business-form.component';
import { CentralisationFormComponent } from './centralisation-form/centralisation-form.component';
import { ConstructionFormComponent } from './construction-form/construction-form.component';
import { HealthFormComponent } from './health-form/health-form.component';
import { HousingRentalFormComponent } from './housing-rental-form/housing-rental-form.component';
import { LeadFormBase } from './lead-form-base';
import { LegalProtectionFormComponent } from './legal-protection-form/legal-protection-form.component';
import { RcHouseholdFormComponent } from './rc-household-form/rc-household-form.component';
import { ThirdPillarFormComponent } from './third-pillar-form/third-pillar-form.component';
import { TravelFormComponent } from './travel-form/travel-form.component';
import { ValuableObjectsFormComponent } from './valuable-objects-form/valuable-objects-form.component';
import { VehicleFormComponent } from './vehicle-form/vehicle-form.component';

@Component({
  selector: 'app-lead-form',
  templateUrl: './lead-form.component.html',
  styleUrls: ['./lead-form.component.css'],
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatOptionModule,
    MatFormFieldModule,
    MatSelectModule,
    AnimalFormComponent,
    BenefitsAccountFormComponent,
    BuildingFormComponent,
    BusinessAccidentFormComponent,
    BusinessConstructionFormComponent,
    BusinessConstructionWarrantyFormComponent,
    BusinessCyberFormComponent,
    BusinessFleetFormComponent,
    BusinessHealthFormComponent,
    BusinessLegalProtectionFormComponent,
    BusinessRentalFormComponent,
    BusinessRetirementFormComponent,
    BusinessTechnicalInstallationFormComponent,
    BusinessThingsFormComponent,
    BusinessTransportFormComponent,
    BusinessVehicleFormComponent,
    RcCompanyFormComponent,
    SharedBusinessFormComponent,
    CentralisationFormComponent,
    ConstructionFormComponent,
    HealthFormComponent,
    HousingRentalFormComponent,
    LegalProtectionFormComponent,
    RcHouseholdFormComponent,
    ThirdPillarFormComponent,
    TravelFormComponent,
    ValuableObjectsFormComponent,
    VehicleFormComponent,
  ],
})
export class LeadFormComponent implements OnInit, OnDestroy {
  @Input()
  customerId!: string | null;

  customer: User;

  isCompanyCustomer = false;

  @Input()
  memberId!: string | null;

  @Input()
  originalProposalId!: string | null;

  @Input()
  lead!: Lead;

  @Input()
  formId: string = 'leadForm';

  @Output()
  formSubmit = new EventEmitter<UpsertLeadRequest>();

  isUpdate = false;

  users: User[] = [];

  familyMembers: FamilyMember[] = [];

  ready: boolean = false;

  @Input()
  isSubmitted: boolean = false;

  comments!: string;

  userPolicies: ProposalDetails[] = [];

  @Output()
  isSubmittedChange = new EventEmitter<boolean>();

  @ViewChild('insuranceSpecificForm') insuranceSpecificForm!: LeadFormBase;

  userIdSubscription!: Subscription;

  insuranceTypeSubscription!: Subscription;

  originalProposalSubscription!: Subscription;

  LeadChangeReasonValues = Object.values(LeadChangeReason);

  getLeadChangeReasonLabel = getLeadChangeReasonLabel;

  selectedChangeReasons = new Set<LeadChangeReason>();

  otherChangeReasonText = '';

  private readonly exclusiveChangeReasonGroups: readonly (readonly LeadChangeReason[])[] =
    EXCLUSIVE_CHANGE_REASON_GROUPS;

  private readonly maxSelectedStandardChangeReasons = 2;

  get displayReasonFormInput(): boolean {
    return this.selectedChangeReasons.has(LeadChangeReason.OTHER);
  }

  isChangeReasonSelected(value: LeadChangeReason): boolean {
    return this.selectedChangeReasons.has(value);
  }

  toggleChangeReason(value: LeadChangeReason): void {
    if (this.selectedChangeReasons.has(value)) {
      this.selectedChangeReasons.delete(value);
      if (value === LeadChangeReason.OTHER) {
        this.otherChangeReasonText = '';
      }
    } else {
      // Removes the change reasons that are exclusive with the new value then adds the new value
      this.exclusiveChangeReasonGroups
        .filter((group) => group.includes(value))
        .forEach((group) =>
          group.forEach((reason) => this.selectedChangeReasons.delete(reason))
        );
      this.selectedChangeReasons.add(value);
      this.enforceChangeReasonSelectionLimit(value);
    }
    this.syncChangeReasonToForm();
  }

  // Makes sure we only have two lead change reasons (excluding OTHER)
  private enforceChangeReasonSelectionLimit(
    preferredValue?: LeadChangeReason
  ): void {
    const standardReasons = [...this.selectedChangeReasons].filter(
      (reason) => reason !== LeadChangeReason.OTHER
    );
    while (standardReasons.length > this.maxSelectedStandardChangeReasons) {
      const reasonToRemove = standardReasons.find(
        (reason) => reason !== preferredValue
      );
      if (!reasonToRemove) {
        break;
      }
      this.selectedChangeReasons.delete(reasonToRemove);
      standardReasons.splice(standardReasons.indexOf(reasonToRemove), 1);
    }
  }

  onOtherChangeReasonTextChange(): void {
    this.syncChangeReasonToForm();
  }

  private buildChangeReasonList(): string[] {
    const reasons: string[] = [];
    for (const value of this.selectedChangeReasons) {
      if (value === LeadChangeReason.OTHER) {
        const text = this.otherChangeReasonText.trim();
        if (text) {
          reasons.push(text);
        }
      } else {
        reasons.push(value);
      }
    }
    return reasons;
  }

  syncChangeReasonToForm(): void {
    const reasons = this.buildChangeReasonList();
    this.formGroup.controls['changeReason'].setValue(
      reasons.length ? reasons : null
    );
    this.formGroup.controls['changeReason'].updateValueAndValidity();
  }

  private readonly changeReasonValidator: ValidatorFn = () => {
    const reasons = this.buildChangeReasonList();
    if (!reasons.length) {
      return { required: true };
    }
    if (
      this.selectedChangeReasons.has(LeadChangeReason.OTHER) &&
      !this.otherChangeReasonText.trim()
    ) {
      return { otherReasonRequired: true };
    }
    return null;
  };

  private resetChangeReasonSelection(): void {
    this.selectedChangeReasons.clear();
    this.otherChangeReasonText = '';
    this.formGroup.controls['changeReason'].reset();
  }

  private loadChangeReasonSelectionFromValue(
    changeReason: string | string[] | null | undefined
  ): void {
    this.selectedChangeReasons.clear();
    this.otherChangeReasonText = '';
    for (const reason of normalizeChangeReason(changeReason)) {
      if (
        isKnownLeadChangeReason(reason) &&
        reason !== LeadChangeReason.OTHER
      ) {
        const hasExclusiveSelection = this.exclusiveChangeReasonGroups.some(
          (group) =>
            group.includes(reason) &&
            group.some((value) => this.selectedChangeReasons.has(value))
        );
        if (hasExclusiveSelection) {
          continue;
        }
        this.selectedChangeReasons.add(reason);
      } else if (reason === LeadChangeReason.OTHER) {
        this.selectedChangeReasons.add(LeadChangeReason.OTHER);
      } else {
        this.selectedChangeReasons.add(LeadChangeReason.OTHER);
        this.otherChangeReasonText = reason;
      }
    }
    this.enforceChangeReasonSelectionLimit();
    this.syncChangeReasonToForm();
  }

  private addChangeReasonValidators(): void {
    this.formGroup.controls['changeReason'].addValidators(
      this.changeReasonValidator
    );
    this.formGroup.controls['changeReason'].updateValueAndValidity();
  }

  constructor(
    private readonly userAuthService: UserAuthService,
    private readonly fb: FormBuilder
  ) {}

  formGroup: FormGroup = this.fb.group({
    leadId: [null],
    userId: this.fb.control('', [Validators.required]),
    memberId: this.fb.control('', [Validators.required]),
    insuranceType: this.fb.control(null, [Validators.required]),
    status: this.fb.control(LeadStatus.PENDING, [Validators.required]),
    metadata: null,
    originalProposalId: this.fb.control(null),
    changeReason: this.fb.control<string[] | null>(null),
    sendEmail: this.fb.control(false),
  });

  ngOnInit(): void {
    this.isUpdate = !!this.lead;
    if (!this.customerId && !this.customer)
      throw new Error(
        'You must provide the customer for which you are creating lead'
      );
    if (!this.customer) {
      this.userAuthService
        .getCustomerDetail({ entityId: this.customerId, entityType: 'USER_ID' })
        .subscribe({
          next: (res) => {
            this.customer = res.data;
            this.setCustomerType();
            this.users = [res.data];
            this.setupForm();
          },
        });
    } else {
      this.setCustomerType();
      this.users = [this.customer];
      this.setupForm();
    }
  }

  getFamilyMembers(userId: string) {
    this.userAuthService.familymembers({ userId }).subscribe({
      next: (res) => {
        this.familyMembers = res.data.familyMemberList;
        if (this.isCompanyCustomer) {
          this.formGroup.controls['memberId'].setValue(
            this.familyMembers.find(
              (x) => x.memberType === FamilyMemberType.SELF
            ).familyMemberId
          );
        }
        this.ready = true;
      },
    });
  }

  getPolicies(entityId: string, entityType: EntityType) {
    this.userPolicies = [];
    this.formGroup.controls['originalProposalId'].reset();
    this.userAuthService
      .AllProposals({
        entityType,
        entityId,
        pageCount: 1,
        pageSize: 10000,
        proposalState: ProposalState.POLICY_CREATED,
        offerType: '',
        policyStatus: PolicyStatus.ACTIVE,
      })
      .subscribe({
        next: (res) => {
          this.userPolicies = !this.isUpdate
            ? res.data.proposalsList
            : res.data.proposalsList?.filter(
                (x: ProposalDetails) =>
                  x.insuranceType ===
                  this.formGroup.controls['insuranceType'].value
              );
          // If the original proposal id is given as input (a specific policy is being handled)
          // We set and disable the insuranceType
          if (this.originalProposalId) {
            const insuranceType = this.userPolicies.find(
              (x) => x.proposalId === this.originalProposalId
            )?.insuranceType;
            if (insuranceType && insuranceType in InsuranceType) {
              this.formGroup.controls['insuranceType'].setValue(insuranceType);
              this.formGroup.controls['insuranceType'].disable();
            }
          }
        },
      });
  }

  insuranceTypes: InsuranceType[] = [
    InsuranceType.HEALTH,
    InsuranceType.COMPLEMENTARY_HEALTH,
    InsuranceType.TRAVEL,
    InsuranceType.CENTRALISATION,
    InsuranceType.RC_HOUSEHOLD,
    InsuranceType.MOTOR_VEHICLE,
    InsuranceType.LEGAL_PROTECTION,
    InsuranceType.BUILDING,
    InsuranceType.CONSTRUCTION,
    InsuranceType.THIRD_PILLAR,
    InsuranceType.ANIMAL,
    InsuranceType.VALUABLE_OBJECTS,
    InsuranceType.HOUSING_RENTAL,
    InsuranceType.BENEFITS_ACCOUNT,
    // Business insurances
    InsuranceType.BUSINESS_ACCIDENT,
    InsuranceType.BUSINESS_ACCIDENT_COMPLEMENTARY,
    InsuranceType.BUSINESS_HEALTH,
    InsuranceType.BUSINESS_RETIREMENT,
    InsuranceType.BUSINESS_COMPANY_THINGS,
    InsuranceType.BUSINESS_RC_COMPANY,
    InsuranceType.BUSINESS_MOTOR_VEHICLE,
    InsuranceType.BUSINESS_FLEET,
    InsuranceType.BUSINESS_TRANSPORT,
    InsuranceType.BUSINESS_TECHNICAL_INSTALLATION,
  ];

  InsuranceTypeEnum = InsuranceType;

  getInsuranceTypeLabel = getInsuranceTypeLabel;

  leadStatusValues = Object.values(LeadStatus);

  getGlobalLeadStatus = getGlobalLeadStatus;

  handleSubmit() {
    this.syncChangeReasonToForm();
    if (!this.isSubmitted && this.formGroup.valid) {
      this.isSubmitted = true;
      this.isSubmittedChange.emit(true);
      this.insuranceSpecificForm.handleSubmit().subscribe({
        next: (res) => {
          if (res) {
            const data = this.formGroup.getRawValue();
            if (data.metadata) {
              data.metadata.comments = this.comments;
            }
            this.formSubmit.emit(data);
            this.resetForm();
          }
        },
        error: () => {
          this.isSubmitted = false;
          this.isSubmittedChange.emit(false);
        },
      });
    }
  }

  isValid() {
    return this.formGroup.valid;
  }

  setupForm() {
    this.formGroup.controls['status']?.setValue(LeadStatus.PENDING);
    if (this.customerId) {
      this.getPolicies(this.customerId, EntityType.MEMBER_ID);
      this.formGroup.controls['userId'].setValue(this.customerId);
      this.formGroup.controls['userId'].disable();
      if (this.memberId) {
        this.formGroup.controls['memberId'].setValue(this.memberId);
      }
      this.getFamilyMembers(this.customerId);
    } else {
      this.userIdSubscription = this.formGroup.controls[
        'userId'
      ].valueChanges.subscribe((value) => {
        this.formGroup.controls['memberId'].reset();
        if (value) {
          this.handleCustomerChange(value);
          this.getFamilyMembers(value);
          this.getPolicies(value, EntityType.MEMBER_ID);
        }
      });
    }
    if (this.originalProposalId) {
      this.formGroup.controls['originalProposalId'].setValue(
        this.originalProposalId
      );
      this.addChangeReasonValidators();
      this.formGroup.controls['originalProposalId'].disable();
    }
    if (this.isUpdate) {
      this.formGroup.controls['leadId'].setValue(this.lead.leadId);
      this.formGroup.controls['insuranceType'].setValue(
        this.lead.insuranceType
      );
      this.formGroup.controls['insuranceType'].disable();
      this.formGroup.controls['status'].setValue(this.lead.status);
      this.formGroup.controls['originalProposalId'].setValue(
        this.lead.originalProposalId
      );
      this.loadChangeReasonSelectionFromValue(this.lead.changeReason);
      if (this.lead.originalProposalId) {
        this.addChangeReasonValidators();
      }
    } else {
      this.insuranceTypeSubscription = this.formGroup.controls[
        'insuranceType'
      ].valueChanges.subscribe((value) => {
        if (value) {
          this.leadStatusValues = getLeadStatusValues(value);
          if (
            value === InsuranceType.CANCELLATION ||
            value === InsuranceType.CENTRALISATION ||
            value === 'POLICY_UPDATE'
          ) {
            this.formGroup.controls['originalProposalId'].setValue(null, {
              emitEvent: false,
            });
          }
        }
      });
    }
    this.originalProposalSubscription = this.formGroup.controls[
      'originalProposalId'
    ].valueChanges.subscribe((value) => {
      if (value) {
        const proposal = this.userPolicies.find((x) => x.proposalId === value);
        if (proposal) {
          this.formGroup.controls['insuranceType'].setValue(
            proposal.insuranceType
          );
        }
        this.addChangeReasonValidators();
      } else {
        this.resetChangeReasonSelection();
        this.formGroup.controls['changeReason'].clearValidators();
      }
    });
    if (this.formGroup.controls['insuranceType'].value) {
      this.leadStatusValues = getLeadStatusValues(
        this.formGroup.controls['insuranceType'].value
      );
    }
  }

  resetForm() {
    this.formGroup.reset();
    this.setupForm();
    if (this.insuranceSpecificForm) this.insuranceSpecificForm.ngOnInit();
  }

  ngOnDestroy(): void {
    if (this.userIdSubscription) this.userIdSubscription.unsubscribe();
    if (this.insuranceTypeSubscription)
      this.insuranceTypeSubscription.unsubscribe();
    if (this.originalProposalSubscription)
      this.originalProposalSubscription.unsubscribe();
  }

  private handleCustomerChange(customerId: string) {
    this.customer = this.users.find((x) => x.userId === customerId);
    this.setCustomerType();
  }

  private setCustomerType() {
    this.isCompanyCustomer = this.customer.type === CustomerAccountType.COMPANY;
    this.insuranceTypes = sortInsuranceTypesByLabel(
      this.isCompanyCustomer ? BUSINESS_INSURANCES : PERSONAL_INSURANCES
    );
  }
}
